import { Alert, Box, Divider, Stack, styled } from "@mui/material";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { IPropertyGroup } from "../../models/propertyGroup";
import { ILocalDisplay } from "../../models/requests/local/localDisplay";
import { ILocalIdPosition, IRouteCreate, IRouteUpdate } from "../../models/route";
import IUser from "../../models/user";
import localService from "../../services/local/LocalService";
import propertyGroupService from "../../services/propertyGroupService/PropertyGroupService";
import userService from "../../services/user/UserService";
import RouteAssignmentSection from "./RouteAssignmentSection";
import RouteBottomBar from "./RouteBottomBar";
import RouteInfoSection from "./RouteInfoSection";
import RouteLocalsSection from "./RouteLocalsSection";
import LocalPickerDrawer from "./LocalPickerDrawer";
import { ILocalDetail } from "../../models/requests/local/localDetail";

const PageWrapper = styled(Box)(() => ({
  display: "flex",
  height: "100%",
  width: "100%",
  overflow: "hidden",
}));

const LeftPanel = styled(Box)(() => ({
  width: "35%",
  overflowY: "auto",
  padding: "28px 24px",
  backgroundColor: "#fff",
  borderRight: "1px solid rgba(0,0,0,0.06)",
}));

const RightPanel = styled(Box)(() => ({
  flex: 1,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "rgba(249, 250, 251, 1)",
  overflow: "hidden",
}));

const buildLocalDisplay = (local: ILocalDetail): ILocalDisplay => ({
  id: local.local.id,
  name: local.local.name,
  profileName: local.profile.name,
  floorName: local.floor.name,
  buildingName: local.building.name,
  tagMacAddress: local.local.tagMacAddress || "☓",
  codes: (local.codes || []).map((code) => code),
});

const sortByPosition = (items: ILocalIdPosition[]) => [...items].sort((a, b) => a.position - b.position);

const normalizePositions = (localIds: number[]): ILocalIdPosition[] =>
  localIds.map((localId, index) => ({ position: index + 1, localId }));

type RouteFormMode = "create" | "edit";

export interface RouteFormInitialValues {
  id?: number;
  name: string;
  propertyGroupId: number;
  localIdPositions: ILocalIdPosition[];
  userIds: number[];
  startTime?: string | null;
  endTime?: string | null;
}

type RouteFormSubmitHandler<TPayload> = (payload: TPayload) => Promise<void>;

interface RouteFormPageProps<TPayload extends IRouteCreate | IRouteUpdate> {
  mode: RouteFormMode;
  initialValues: RouteFormInitialValues;
  errorMessage: string | null;
  onSubmit: RouteFormSubmitHandler<TPayload>;
  onCancel: () => void;
}

const RouteFormPage = <TPayload extends IRouteCreate | IRouteUpdate>({
  mode,
  initialValues,
  errorMessage,
  onSubmit,
  onCancel,
}: RouteFormPageProps<TPayload>) => {
  const [routeName, setRouteName] = useState(initialValues.name);
  const [propertyGroupId, setPropertyGroupId] = useState(initialValues.propertyGroupId);
  const [startTime, setStartTime] = useState<string | null>(initialValues.startTime ?? null);
  const [endTime, setEndTime] = useState<string | null>(initialValues.endTime ?? null);

  const [propertyGroups, setPropertyGroups] = useState<IPropertyGroup[]>([]);
  const [availableUsers, setAvailableUsers] = useState<IUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<IUser[]>([]);
  const [userSearchText, setUserSearchText] = useState("");

  const [availableLocals, setAvailableLocals] = useState<ILocalDisplay[]>([]);
  const [selectedLocals, setSelectedLocals] = useState<ILocalDisplay[]>([]);

  const [isLocalPickerOpen, setIsLocalPickerOpen] = useState(false);

  useEffect(() => {
    propertyGroupService.GetPropertyGroups().then(setPropertyGroups);
    userService.GetUsersByCompany().then((users) => {
      setAvailableUsers(users);
      setSelectedUsers(users.filter((user) => initialValues.userIds.includes(user.id)));
    });
  }, [initialValues.userIds]);

  useEffect(() => {
    if (propertyGroupId <= 0) {
      setAvailableLocals([]);
      setSelectedLocals([]);
      return;
    }

    localService.GetLocalsByPropertyGroup(propertyGroupId).then((localsData) => {
      const localDisplays = localsData.map(buildLocalDisplay);
      setAvailableLocals(localDisplays);

      const orderedLocalIds = sortByPosition(initialValues.localIdPositions).map((x) => x.localId);
      const selectedInOrder = orderedLocalIds
        .map((id) => localDisplays.find((local) => local.id === id))
        .filter(Boolean) as ILocalDisplay[];

      setSelectedLocals(selectedInOrder);
    });
  }, [propertyGroupId, initialValues.localIdPositions]);

  const hasInvalidSchedule = useMemo(() => {
    const hasStart = !!startTime;
    const hasEnd = !!endTime;
    if (hasStart !== hasEnd) return true;
    if (!hasStart && !hasEnd) return false;
    return startTime === endTime;
  }, [startTime, endTime]);

  const selectedLocalIdSet = useMemo(() => new Set(selectedLocals.map((local) => local.id)), [selectedLocals]);

  const localIdPositions = useMemo(
    () => normalizePositions(selectedLocals.map((local) => local.id)),
    [selectedLocals]
  );

  const areRouteValuesModified = useMemo(() => {
    if (mode !== "edit") return true;
    const routeState = {
      id: initialValues.id,
      name: routeName.trim(),
      propertyGroupId,
      localIdPositions,
      userIds: selectedUsers.map((user) => user.id),
      startTime,
      endTime,
    };

    return JSON.stringify(routeState) !== JSON.stringify(initialValues);
  }, [
    mode,
    routeName,
    propertyGroupId,
    localIdPositions,
    selectedUsers,
    startTime,
    endTime,
  ]);

  const isSubmitDisabled =
    !areRouteValuesModified ||
    !routeName.trim() ||
    propertyGroupId <= 0 ||
    selectedLocals.length === 0 ||
    selectedUsers.length === 0 ||
    hasInvalidSchedule;

  const openPicker = () => setIsLocalPickerOpen(true);
  const closePicker = () => setIsLocalPickerOpen(false);

  const onRemoveLocal = (localId: number) => {
    setSelectedLocals((prev) => prev.filter((local) => local.id !== localId));
  };

  const onSelectLocalFromPicker = (local: ILocalDisplay) => {
    setSelectedLocals((prev) => {
      if (prev.some((l) => l.id === local.id)) return prev;
      return [...prev, local];
    });
  };

  const onReorderLocals = (nextLocals: ILocalDisplay[]) => {
    setSelectedLocals(nextLocals);
  };

  const addUser = (user: IUser) => {
    setSelectedUsers((prev) => [...prev, user]);
    setUserSearchText("");
  };

  const removeUser = (userId: number) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const submitForm = async () => {
    const commonPayload = {
      name: routeName.trim(),
      propertyGroupId,
      localIdPositions,
      userIds: selectedUsers.map((user) => user.id),
      startTime,
      endTime,
    };

    if (mode === "edit" && initialValues.id) {
      await onSubmit({ id: initialValues.id, ...commonPayload } as TPayload);
      return;
    }

    await onSubmit(commonPayload as TPayload);
  };

  const onFormSubmit = async (event: FormEvent) => {
    event.preventDefault();
    await submitForm();
  };

  return (
    <PageWrapper onSubmit={onFormSubmit}>
      <LeftPanel>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Stack spacing={0}>
          <RouteInfoSection
            mode={mode}
            routeName={routeName}
            propertyGroupId={propertyGroupId}
            propertyGroups={propertyGroups}
            startTime={startTime}
            endTime={endTime}
            hasInvalidSchedule={hasInvalidSchedule}
            onRouteNameChange={setRouteName}
            onPropertyGroupChange={(value) => setPropertyGroupId(value)}
            onStartTimeChange={setStartTime}
            onEndTimeChange={setEndTime}
          />

          <Divider sx={{ mt: 3 }} />

          <RouteAssignmentSection
            availableUsers={availableUsers}
            selectedUsers={selectedUsers}
            userSearchText={userSearchText}
            onUserSearchTextChange={setUserSearchText}
            onAddUser={addUser}
            onRemoveUser={removeUser}
          />
        </Stack>
      </LeftPanel>

      <RightPanel>
        <RouteLocalsSection
          propertyGroupId={propertyGroupId}
          selectedLocals={selectedLocals}
          onOpenPicker={() => openPicker()}
          onRemoveLocal={onRemoveLocal}
          onReorderLocals={onReorderLocals}
        />

        <RouteBottomBar
          mode={mode}
          localsCount={selectedLocals.length}
          isSubmitDisabled={isSubmitDisabled}
          onCancel={onCancel}
          onSubmit={submitForm}
        />
      </RightPanel>

      <LocalPickerDrawer
        open={isLocalPickerOpen}
        onClose={closePicker}
        availableLocals={availableLocals}
        selectedLocalIdSet={selectedLocalIdSet}
        onSelect={(local) => {
          onSelectLocalFromPicker(local);
          closePicker();
        }}
      />
    </PageWrapper>
  );
};

export default RouteFormPage;